import Ajv from "ajv"
import {
  FilterCalendrical,
  FilterInequality,
  RangeTemporalFilter,
  SimpleTemporalFilter,
  SystemLink,
  useFilterStore,
} from "src/stores/filterStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import {
  MILLISECONDS_PER_DAY,
  MILLISECONDS_PER_MONTH,
  MILLISECONDS_PER_YEAR,
} from "src/util/constants/temporal"
import {
  ENTITY_TYPE,
  PART_OF_WORK_ORDER,
  QR_CODE,
  RAISED_THROUGH,
  RFID,
  WORK_TYPE,
} from "src/util/enums/filters"
import { getISOTimeString } from "src/util/functions/time"
import { showErrorNotification } from "src/util/functions/notifications"
import { numberToPriorityClass } from "src/util/functions/misc"
import { IssuesOnItem, IssueTableData } from "src/util/types/dataModels/issue"
import { ItemTableData } from "src/util/types/dataModels/item"
import { PriorityClass } from "src/util/types/misc"
import { onBeforeMount, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { WorkOrderTableData } from "src/util/types/dataModels/workOrder"
import { RowData } from "src/util/types/components/sfxTable"
import {
  CorrectiveWorkOnItem,
  PerformedWorkOnItemCardData,
  PerformedWorkOnItemTableData,
} from "src/util/types/dataModels/performedWork"

/**
 * Returns a T type object which contains the filters for the relevant page.
 *
 * If the url is manipulated by the user upto a point which it does not adhere to the
 * provided schema the function would return 'undefined'
 *
 * @param schema The schema which will be used to validate the param object
 */
export function useUrlFilter<T>(schema: object) {
  const state = ref<T | undefined>()
  const hasParams = ref<boolean>(false)

  // Check if the param object adhere to the provided schema
  function check(newState: unknown): newState is T {
    const ajv = new Ajv({ allErrors: true })
    const valid = ajv.validate(schema, newState)
    return Boolean(valid)
  }

  function update(newState: unknown) {
    if (check(newState)) {
      state.value = newState as T
    }
  }

  onBeforeMount(async () => {
    try {
      // Fetch the current url params
      const params = useRoute().query.filters
      // No parameters found in the url
      if (!params?.length) return
      // true means URL has parameters with the key "filters" (could be valid or invalid)
      hasParams.value = true

      if (typeof params === "string") {
        const obj = JSON.parse(params)
        update(obj)
      }
    } catch (error) {
      // User has manipulated the url params and now it does not pass the schema validation
      Log.error({
        message: "The parameters in the url are invalid",
        error: error,
      })
    }
  })

  return { state, hasParams }
}

export function filterEntitiesWithRFID(item: ItemTableData) {
  const filterStore = useFilterStore()

  if (filterStore.itemsPageFilters.hasRfid === RFID.EITHER) {
    return true
  } else if (filterStore.itemsPageFilters.hasRfid === RFID.YES) {
    return Boolean(item.hasRfidIdentifier)
  } else {
    return !item.hasRfidIdentifier
  }
}

export function filterEntitiesWithQRCode(item: ItemTableData) {
  const filterStore = useFilterStore()

  if (filterStore.itemsPageFilters.hasQrCode === QR_CODE.EITHER) {
    return true
  } else if (filterStore.itemsPageFilters.hasQrCode === QR_CODE.YES) {
    return Boolean(item.hasQrIdentifier)
  } else {
    return !item.hasQrIdentifier
  }
}

/**
 * Filter based on entity type
 *
 * @param entityType "area" | "item" | "either"
 * @param isItem true | false
 */
export function filterWithEntityType(entityType: ENTITY_TYPE, isItem: boolean) {
  if (entityType === ENTITY_TYPE.EITHER) {
    return true
  } else if (entityType === ENTITY_TYPE.ITEM) {
    return isItem
  } else {
    return !isItem
  }
}

/**
 * Filter based on Work Order Type
 *
 * @param workType "regular" | "corrective" | "either"
 * @param isCorrective true | false
 */
export function filterWithWorkOrderType(workType: WORK_TYPE, isCorrective: boolean) {
  if (workType === WORK_TYPE.EITHER) {
    return true
  } else if (workType === WORK_TYPE.CORRECTIVE) {
    return isCorrective
  } else {
    return !isCorrective
  }
}

export function filterIssuesRaisedThroughPerformedWork(issue: IssueTableData) {
  const filterStore = useFilterStore()

  if (filterStore.issuesPageFilters.raisedThrough === RAISED_THROUGH.EITHER) {
    return true
  } else if (filterStore.issuesPageFilters.raisedThrough === RAISED_THROUGH.YES) {
    return Boolean(issue.issueData.raised_from)
  } else {
    return !issue.issueData.raised_from
  }
}

export function filterPerformedWorkPartOfWorkOrder(
  isPartOf: PART_OF_WORK_ORDER,
  inWorkOrder: boolean
) {
  if (isPartOf === PART_OF_WORK_ORDER.EITHER) {
    return true
  } else if (isPartOf === PART_OF_WORK_ORDER.YES) {
    return inWorkOrder
  } else {
    return !inWorkOrder
  }
}

export function filterEntitiesWithSimpleTemporal(
  temporalData: SimpleTemporalFilter | undefined,
  createdDts: string[]
) {
  if (!temporalData || temporalData.number === undefined) return true

  const today = Date.now()
  const duration = today - getDurationInMilliseconds(temporalData.number, temporalData.calendrical)
  let isValid: boolean = false
  let timeLineNoneDataCount: number = 0

  for (const createdDt of createdDts) {
    const createdDate = new Date(createdDt).getTime()

    isValid = isEntityInDuration(duration, createdDate, temporalData.operator)

    // This entity list is already sorted according to the created_dt
    // If the user wants to check only the latest entity, we have already done that, so break the loop
    if (temporalData.timeLine === "latest") {
      break
    }
    // If the user selected 'any', it means any entity which satisfies the conditions
    // If at-least one entity satisfies the conditions, break the loop
    else if (temporalData.timeLine === "any" && isValid) {
      break
    }
    // If the user selected 'none', it means the entity should not be raised during the given period
    else if (temporalData.timeLine === "none" && !isValid) {
      timeLineNoneDataCount++
    }
  }
  // All the entities should satisfy the conditions
  if (timeLineNoneDataCount === createdDts.length) return true

  return isValid
}

function isEntityInDuration(duration: number, createdDate: number, operator: FilterInequality) {
  switch (operator) {
    case "less-than": {
      return duration < createdDate
    }
    case "less-than-equal": {
      return duration <= createdDate
    }
    case "greater-than": {
      return duration > createdDate
    }
    case "greater-than-equal": {
      return duration >= createdDate
    }
  }
}

function getDurationInMilliseconds(number: number, calendricalValue: FilterCalendrical) {
  switch (calendricalValue) {
    case "days": {
      return number * MILLISECONDS_PER_DAY
    }
    case "months": {
      return number * MILLISECONDS_PER_MONTH
    }
    case "years": {
      return number * MILLISECONDS_PER_YEAR
    }
    // No default
  }
}

export function filterEntitiesWithRangeTemporal(
  temporalData: RangeTemporalFilter | undefined,
  createdDts: string[]
) {
  // If any of the following fields are not filled, no point of executing the rest
  if (!temporalData || !temporalData.startDate || !temporalData.endDate) return true

  const startTime = getISOTimeString(temporalData.startDate, temporalData.startTime)
  const endTime = getISOTimeString(temporalData.endDate, temporalData.endTime)

  if (!startTime || !endTime) return false
  if (startTime >= endTime) {
    const { t } = useI18n()
    showErrorNotification(t("sfxErrorMsg.error_range_end_after_range_start"))
    return false
  }

  switch (temporalData.timeLine) {
    case "latest": {
      // Only the latest entity should pass the conditions
      return startTime <= createdDts[0] && createdDts[0] <= endTime
    }
    case "any": {
      // At least one entity should pass the conditions
      return createdDts?.some((el) => startTime <= el && el <= endTime)
    }
    case "none": {
      // None of the entities should pass the conditions
      return !createdDts?.some((el) => startTime <= el && el <= endTime)
    }
  }
}

export function filterEntityWithWorkDomain(
  storeWorkDomainUUIDs: string[] | undefined,
  workDomainUUIDs: string[] // The list could contain duplicates
) {
  if (!storeWorkDomainUUIDs || storeWorkDomainUUIDs.length === 0) return true

  const uniqueWorkDomainUUIDs = new Set(workDomainUUIDs)

  for (const uuid of storeWorkDomainUUIDs) {
    if (uniqueWorkDomainUUIDs.has(uuid)) {
      return true
    }
  }
  return false
}

export function filterEntityWithPriority(
  priorities: PriorityClass[] | undefined,
  priorityNumbers: number[]
) {
  if (!priorities || priorities.length === 0) return true

  const priorityClasses = new Set(priorityNumbers.map((el) => numberToPriorityClass(el)))
  for (const priority of priorities) {
    if (priorityClasses.has(priority)) {
      return true
    }
  }
  return false
}

function filterIssuesWithIssueState(
  issueStates: string[] | undefined,
  issueData: IssuesOnItem[],
  correctiveWork?: CorrectiveWorkOnItem | undefined
) {
  if (!issueStates || issueStates.length === 0) return true

  // There can be multiple issue states
  for (const issueState of issueStates) {
    const result = getIsIssueInState(issueState, issueData, correctiveWork)
    if (result) {
      return result
    }
  }
  return false
}

function getIsIssueInState(
  issueState: string,
  issueData: IssuesOnItem[],
  correctiveWork?: CorrectiveWorkOnItem | undefined
) {
  switch (issueState) {
    case "in_work_order": {
      if (issueData.some((issue) => issue.corrective_work_order)) {
        return true
      }
      break
    }
    case "raised": {
      if (issueData.some((issue) => !issue.conclusion)) {
        return true
      }
      break
    }
    case "corrected": {
      if (correctiveWork && issueData.some((issue) => !issue.conclusion)) {
        return true
      }
      break
    }
    case "closed": {
      if (issueData.some((issue) => issue.conclusion === "closed")) {
        return true
      }
      break
    }
    case "corrected_closed": {
      if (issueData.some((issue) => issue.conclusion === "closed_resolved")) {
        return true
      }
      break
    }
    default:
      throw new TypeError(`Unknown issueState in filter: '${issueState}'`)
  }
}

export function filterWithEntityName(name: string | undefined, entityNames: string[]) {
  if (!name) return true

  const textInLowerCase = name.toLowerCase()

  for (const entityName of entityNames) {
    if (wildcardMatchRegExp(entityName.toLowerCase(), textInLowerCase)) {
      return true
    }
  }
  return false
}

/**
 * Convert wildcard pattern to a regular expression pattern
 * A  `*` in the pattern, will match with 0 or more characters, and '?' with one or more characters.
 *
 * NOTE:
 * This function constructs a regular expression from user input. That means it is susceptible to
 * a DoS attack on malicious inputs. The output of this function is used only for local filtering
 * and never sent server-side. That means there is no system-risk.
 *
 * This function is based on https://www.geeksforgeeks.org/wildcard-pattern-matching-in-javascript/
 *
 * @param text It is the main string that is to be checked
 * @param pattern It is the wildcard pattern that is used to check the specific pattern in the text
 */
function wildcardMatchRegExp(text: string, pattern: string): boolean {
  // Convert wildcard pattern to a regular expression pattern
  const regexPattern = new RegExp("^.*" + pattern.replaceAll("?", ".").replaceAll("*", ".*") + ".*")

  // Test if the text matches the regular expression pattern
  return regexPattern.test(text)
}

export function filterItemsBasedOnIssues(item: ItemTableData) {
  const filterStore = useFilterStore()

  // If there are no issue filters, then the filter is valid.
  if (!filterStore.itemsPageFilters.hasIssueFilters) {
    return true
  }

  // If we have no issues then the filter was invalid.
  if (!item.issueData || item.issueData.length === 0) {
    return false
  }

  let isValid: boolean = false

  // Filter by work domains
  isValid = filterEntityWithWorkDomain(
    filterStore.itemsPageFilters.issuesFilter.workDomain,
    item.issueData.map((el) => el.work_domain)
  )
  if (!isValid) return false

  // By now we know item has issues. It means there should be issuePriorityData
  // This is just to avoid a typescript error
  if (!item.issuePriorityData) return false
  // Filter by issue priority
  isValid = filterEntityWithPriority(
    filterStore.itemsPageFilters.issuesFilter.priority,
    item.issuePriorityData.map((el) => el.risk_factor)
  )
  if (!isValid) return false

  // Filter by designation
  isValid = filterWithEntityName(
    filterStore.itemsPageFilters.issuesFilter.designation,
    item.issueData.map((el) => el.title)
  )
  if (!isValid) return false

  // Filter by temporal data
  if (filterStore.itemsPageFilters.issuesFilter.isRangeTemporal) {
    isValid = filterEntitiesWithRangeTemporal(
      filterStore.itemsPageFilters.issuesFilter.raisedRange,
      item.issueData.map((el) => el.created_dt)
    )
    if (!isValid) return false
  } else {
    isValid = filterEntitiesWithSimpleTemporal(
      filterStore.itemsPageFilters.issuesFilter.raisedSimple,
      item.issueData.map((el) => el.created_dt)
    )
    if (!isValid) return false
  }

  // Filter by issue state
  isValid = filterIssuesWithIssueState(
    filterStore.itemsPageFilters.issuesFilter.state,
    item.issueData
    // TODO: Add corrective work when we have a proper solution to fetch those data
  )
  return isValid
}

export function filterItemsBasedOnRegularWork(item: ItemTableData) {
  const filterStore = useFilterStore()

  // If there are no regular work filters, then the filter is valid.
  if (!filterStore.itemsPageFilters.hasRegularWorkFilters) {
    return true
  }

  // If we have no regular work then the filter was invalid.
  if (!item.regularWork || item.regularWork.length === 0) {
    return false
  }

  let isValid: boolean = false

  // Filter by work domains
  isValid = filterEntityWithWorkDomain(
    filterStore.itemsPageFilters.regularWorkFilter.workDomain,
    item.regularWork.map((el) => el.work_domain)
  )
  if (!isValid) return false

  // Filter by designation
  isValid = filterWithEntityName(
    filterStore.itemsPageFilters.regularWorkFilter.designation,
    item.regularWork.map((el) => el.title)
  )
  if (!isValid) return false

  // Filter by temporal data
  if (filterStore.itemsPageFilters.regularWorkFilter.isRangeTemporal) {
    isValid = filterEntitiesWithRangeTemporal(
      filterStore.itemsPageFilters.regularWorkFilter.performedRange,
      item.regularWork.map((el) => el.created_dt)
    )
  } else {
    isValid = filterEntitiesWithSimpleTemporal(
      filterStore.itemsPageFilters.regularWorkFilter.performedSimple,
      item.regularWork.map((el) => el.created_dt)
    )
  }
  return isValid
}

export function filterIssuesBasedOnRegularWorkRaisedFrom(issue: IssueTableData) {
  const filterStore = useFilterStore()

  // If there are no regular work filters, then the filter is valid.
  if (!filterStore.issuesPageFilters.hasRegularWorkFilters) return true

  // If we have no regular work data then the filter is invalid.
  if (!issue.regularWork) return false

  let isValid: boolean = false

  // Filter by designation
  if (filterStore.issuesPageFilters.regularWorkFilter.designation) {
    const textInLowerCase =
      filterStore.issuesPageFilters.regularWorkFilter.designation.toLowerCase()
    isValid = wildcardMatchRegExp(issue.regularWork.title.toLowerCase(), textInLowerCase)
    if (!isValid) return false
  }

  // Filter by temporal data
  if (filterStore.issuesPageFilters.regularWorkFilter.isRangeTemporal) {
    isValid = filterEntitiesWithRangeTemporal(
      filterStore.issuesPageFilters.regularWorkFilter.performedRange,
      [issue.regularWork.created_dt]
    )
  } else {
    isValid = filterEntitiesWithSimpleTemporal(
      filterStore.issuesPageFilters.regularWorkFilter.performedSimple,
      [issue.regularWork.created_dt]
    )
  }
  return isValid
}

export function filterItemsBasedOnCorrectiveWork(item: ItemTableData) {
  const filterStore = useFilterStore()

  // If there are no corrective work filters, then the filter is valid.
  if (!filterStore.itemsPageFilters.hasCorrectiveWorkFilters) {
    return true
  }

  // If we have no corrective work then the filter was invalid.
  if (!item.correctiveWork || item.correctiveWork.length === 0) {
    return false
  }

  let isValid: boolean = false

  // Filter by work domains
  isValid = filterEntityWithWorkDomain(
    filterStore.itemsPageFilters.correctiveWorkFilter.workDomain,
    item.correctiveWork.map((el) => el.work_domain)
  )
  if (!isValid) return false

  // Filter by designation
  isValid = filterWithEntityName(
    filterStore.itemsPageFilters.correctiveWorkFilter.designation,
    item.correctiveWork.map((el) => el.title)
  )
  if (!isValid) return false

  // Filter by temporal data
  if (filterStore.itemsPageFilters.correctiveWorkFilter.isRangeTemporal) {
    isValid = filterEntitiesWithRangeTemporal(
      filterStore.itemsPageFilters.correctiveWorkFilter.performedRange,
      item.correctiveWork.map((el) => el.created_dt)
    )
  } else {
    isValid = filterEntitiesWithSimpleTemporal(
      filterStore.itemsPageFilters.correctiveWorkFilter.performedSimple,
      item.correctiveWork.map((el) => el.created_dt)
    )
  }
  return isValid
}

export function filterIssuesBasedOnItems(issue: IssueTableData) {
  const filterStore = useFilterStore()

  // If there are no item filters, then the filter is valid.
  if (!filterStore.issuesPageFilters.hasItemFilters) return true

  let isValid: boolean = false

  // Filter by item class
  if (filterStore.issuesPageFilters.itemFilter.itemClass) {
    if (!issue.itemClassData) return false
    isValid = filterStore.issuesPageFilters.itemFilter.itemClass.includes(issue.itemClassData?.id)
    if (!isValid) return false
  }

  // Filter by designation
  if (filterStore.issuesPageFilters.itemFilter.designation) {
    if (!issue.actionableItem?.designation) return false
    const textInLowerCase = filterStore.issuesPageFilters.itemFilter.designation.toLowerCase()
    return wildcardMatchRegExp(issue.actionableItem.designation.toLowerCase(), textInLowerCase)
  }

  return isValid
}

export function filterItemsBasedOnWorkOrders(item: ItemTableData) {
  // TODO: This fn needs to be updated(or create a duplicate fn) to allow corrective work order data to be filtered.
  // Until we have offline mode data layer we have decided to forgo filtering corrective work data in ItemSelect page.

  const filterStore = useFilterStore()

  // If there are no work order filters, then the filter is valid.
  if (!filterStore.itemsPageFilters.hasWorkOrderFilters) {
    return true
  }

  // If we have no work orders then the filter is invalid.
  if (!item.regularWorkOrder || item.regularWorkOrder.length === 0) {
    return false
  }

  let isValid: boolean = false

  // Filter by work domains
  isValid = filterEntityWithWorkDomain(
    filterStore.itemsPageFilters.workOrderFilter.workDomain,
    item.regularWorkOrder.map((el) => el.work_domain)
  )
  if (!isValid) return false

  // By now we know item has work orders. It means there should be workOrderPriorityData
  // This is just to avoid a typescript error
  if (!item.workOrderPriorityData) return false
  // Filter by work order priority
  isValid = filterEntityWithPriority(
    filterStore.itemsPageFilters.workOrderFilter.priority,
    item.workOrderPriorityData.map((el) => el.rank)
  )
  if (!isValid) return false

  // Filter by designation
  isValid = filterWithEntityName(
    filterStore.itemsPageFilters.workOrderFilter.designation,
    item.regularWorkOrder.map((el) => el.designation)
  )
  if (!isValid) return false

  // Filter by temporal data
  if (filterStore.itemsPageFilters.workOrderFilter.isRangeTemporal) {
    isValid = filterEntitiesWithRangeTemporal(
      filterStore.itemsPageFilters.workOrderFilter.startDateRange,
      item.regularWorkOrder.map((el) => el.created_dt)
    )
    if (!isValid) return false
  } else {
    isValid = filterEntitiesWithSimpleTemporal(
      filterStore.itemsPageFilters.workOrderFilter.startDateSimple,
      item.regularWorkOrder.map((el) => el.created_dt)
    )
    if (!isValid) return false
  }

  // Filter by work order type
  // TODO: Include corrective work order filtering for item when available
  // TODO: As agreed at the standup this filtering is restricted to regular work until we have a proper data fetching solution
  return filterWithWorkOrderType(filterStore.itemsPageFilters.workOrderFilter.workType, false)
}

export function filterIssuesBasedOnSelectedValues(issue: IssueTableData) {
  const filterStore = useFilterStore()

  let isValid: boolean = false

  // Filter by system link entity ID
  if (filterStore.issuesPageFilters.systemLink?.entityId) {
    isValid = issue.actionableItem?.id === filterStore.issuesPageFilters.systemLink?.entityId
    if (!isValid) return false
  }

  // Filter by work domains
  isValid = filterEntityWithWorkDomain(filterStore.issuesPageFilters.workDomain, [
    issue.issueData.work_domain,
  ])
  if (!isValid) return false

  // Filter by entity type
  // TODO: We need to update the params when we have areas
  isValid = filterWithEntityType(filterStore.issuesPageFilters.entityType, true)
  if (!isValid) return false

  // Filter by raised from performed work
  isValid = filterIssuesRaisedThroughPerformedWork(issue)
  if (!isValid) return false

  // Filter by issue priority
  isValid = filterEntityWithPriority(filterStore.issuesPageFilters.priority, [
    issue.priorityData.risk_factor,
  ])
  if (!isValid) return false

  // Filter by issue state
  isValid = filterIssuesWithIssueState(
    filterStore.issuesPageFilters.state,
    [issue.issueData],
    issue.correctiveWork
  )
  return isValid
}

export function filterWorkOrdersBasedOnSelectedValues(workOrder: RowData<WorkOrderTableData>) {
  const filterStore = useFilterStore()

  let isValid: boolean = false

  // Filter by system link entity ID
  isValid = filterWorkOrdersBySystemLink(filterStore.workOrderPageFilters.systemLink, workOrder)
  if (!isValid) return false

  // Filter by assigned
  if (filterStore.workOrderPageFilters.assigned) {
    const userStore = useUserStore()
    isValid = workOrder.workOrderData.assigned_users.includes(userStore.id)
    if (!isValid) return false
  }

  // Filter by work domains
  if (workOrder.workDomainData) {
    isValid = filterEntityWithWorkDomain(filterStore.workOrderPageFilters.workDomain, [
      workOrder.workDomainData.id,
    ])
    if (!isValid) return false
  }

  // Filter by work order type
  isValid = filterWithWorkOrderType(
    filterStore.workOrderPageFilters.workType,
    workOrder.isCorrectiveWork
  )
  if (!isValid) return false

  // Filter by entity type
  // TODO: We need to update the params when we have areas
  isValid = filterWithEntityType(filterStore.workOrderPageFilters.entityType, true)
  if (!isValid) return false

  // Filter by work order priority
  isValid = filterEntityWithPriority(filterStore.workOrderPageFilters.priority, [
    workOrder.priorityData.rank,
  ])
  if (!isValid) return false

  // Filter by work order state
  if (
    filterStore.workOrderPageFilters.state &&
    filterStore.workOrderPageFilters.state?.length > 0
  ) {
    // Ideally this should not happen
    if (!workOrder.workOrderState) return false

    return filterStore.workOrderPageFilters.state.includes(workOrder.workOrderState)
  }

  return isValid
}

function filterWorkOrdersBySystemLink(
  systemLink: SystemLink | undefined,
  workOrder: RowData<WorkOrderTableData>
): boolean {
  if (!systemLink) return true

  if (workOrder.isItem) {
    return workOrder.isCorrectiveWork
      ? workOrder.issuesData.some((el) => el.actionable_item === systemLink.entityId)
      : workOrder.itemsData.some((el) => el.id === systemLink.entityId)
  } else {
    // TODO: Handle the area case
    return false
  }
}

export function filterPerformedWorkBasedOnSelectedValues(
  work: RowData<PerformedWorkOnItemTableData> | PerformedWorkOnItemCardData
) {
  const filterStore = useFilterStore()

  let isValid: boolean = false

  // Filter by work domains
  isValid = filterEntityWithWorkDomain(filterStore.performedWorkPageFilters.workDomain, [
    work.performedWorkOnItemData.work_domain,
  ])
  if (!isValid) return false

  // Filter by entity type
  // TODO: We need to update the params when we have areas
  isValid = filterWithEntityType(filterStore.performedWorkPageFilters.entityType, true)
  if (!isValid) return false

  // Filter by work  type
  isValid = filterWithWorkOrderType(
    filterStore.performedWorkPageFilters.workType,
    work.isCorrectiveWork
  )
  if (!isValid) return false

  // Filter by part of a work order
  isValid = filterPerformedWorkPartOfWorkOrder(
    filterStore.performedWorkPageFilters.partOfWorkOrder,
    Boolean(work.performedWorkOnItemData.work_order)
  )
  if (!isValid) return false

  // Filter by item class
  if (
    filterStore.performedWorkPageFilters.itemClass &&
    filterStore.performedWorkPageFilters.itemClass.length > 0
  ) {
    if (!work.itemClassData) return false
    isValid = filterStore.performedWorkPageFilters.itemClass.includes(work.itemClassData?.id)
    if (!isValid) return false
  }

  // TODO: Implement when we have areas
  // Filter by area attribute set
  // if (
  //   filterStore.performedWorkPageFilters.areaAttributeSet &&
  //   filterStore.performedWorkPageFilters.areaAttributeSet > 0
  // ) {
  // }

  // Filter by performed work conclusion
  if (
    filterStore.performedWorkPageFilters.state &&
    filterStore.performedWorkPageFilters.state?.length > 0
  ) {
    // Ideally this should not happen
    if (!work.performedWorkState) return false

    return filterStore.performedWorkPageFilters.state.includes(work.performedWorkState)
  }

  return isValid
}
